import { ChangeDetectorRef, EventEmitter, Injectable, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { isObservable } from 'rxjs';
import { Subscription } from 'rxjs';
import { LocalizationService } from './localization.service';
import { equals, isDefined } from '../helpers/helper';
import { LanguageChangeEvent, LocalizationChangeEvent } from './localizaton.model';

@Injectable({providedIn:'root'})
@Pipe({
    name: 'localize',
    pure: false // required to update the value when the promise is resolved
})
export class LocalizationPipe implements PipeTransform, OnDestroy {
    value: string = '';
    lastKey: string | null = null;
    lastParams: any[] = [];
    onLocalizationChange: Subscription | undefined;
    onLanguageChange: Subscription | undefined;
    onDefaultLanguageChange: Subscription | undefined;

    constructor(private _localizationService: LocalizationService, private _ref: ChangeDetectorRef) {
    }

    updateValue(key: string, interpolateParams?: Object, localizations?: any): void {
        let onLocalization = (res: string) => {
            this.value = res !== undefined ? res : key;
            this.lastKey = key;
            this._ref.markForCheck();
        };
        if (localizations) {
            let res = this._localizationService.getParsedResult(localizations, key, interpolateParams);
            if (isObservable(res.subscribe)) {
                res.subscribe(onLocalization);
            } else {
                onLocalization(res);
            }
        }
        this._localizationService.get(key, interpolateParams).subscribe(onLocalization);
    }

    transform(query: string, ...args: any[]): any {
        if (!query || !query.length) {
            return query;
        }

        // if we ask another time for the same key, return the last value
        // if (equals(query, this.lastKey) && equals(args, this.lastParams)) {
        //     return this.value;
        // }

        let interpolateParams: Object | undefined = undefined;
        if (isDefined(args[0]) && args.length) {
            if (typeof args[0] === 'string' && args[0].length) {
                // we accept objects written in the template such as {n:1}, {'n':1}, {n:'v'}
                // which is why we might need to change it to real JSON objects such as {"n":1} or {"n":"v"}
                let validArgs: string = args[0]
                    .replace(/(\')?([a-zA-Z0-9_]+)(\')?(\s)?:/g, '"$2":')
                    .replace(/:(\s)?(\')(.*?)(\')/g, ':"$3"');
                try {
                    interpolateParams = JSON.parse(validArgs);
                } catch (e) {
                    throw new SyntaxError(`Wrong parameter in TranslatePipe. Expected a valid Object, received: ${args[0]}`);
                }
            } else if (typeof args[0] === 'object' && !Array.isArray(args[0])) {
                interpolateParams = args[0];
            }
        }

        // store the query, in case it changes
        this.lastKey = query;

        // store the params, in case they change
        this.lastParams = args;

        // set the value
        this.updateValue(query, interpolateParams);

        // if there is a subscription to onLangChange, clean it
        this._dispose();

        // subscribe to onLocalizationChange event, in case the localizations change
        if (!this.onLocalizationChange) {
            this.onLocalizationChange = this._localizationService.hub.onLocalizationChange.subscribe((event: LocalizationChangeEvent) => {
                if (this.lastKey && event.language === this._localizationService.hub.currentLanguage) {
                    this.lastKey = null;
                    this.updateValue(query, interpolateParams, event.localizations);
                }
            });
        }

        // subscribe to onLangChange event, in case the language changes
        if (!this.onLanguageChange) {
            this.onLanguageChange = this._localizationService.hub.onLanguageChange.subscribe((event: LanguageChangeEvent) => {
                if (this.lastKey) {
                    this.lastKey = null; // we want to make sure it doesn't return the same value until it's been updated
                    this.updateValue(query, interpolateParams, event.localizations);
                }
            });
        }

        // subscribe to onDefaultLangChange event, in case the default language changes
        if (!this.onDefaultLanguageChange) {
            this.onDefaultLanguageChange = this._localizationService.hub.onDefaultLanguageChange.subscribe(() => {
                if (this.lastKey) {
                    this.lastKey = null; // we want to make sure it doesn't return the same value until it's been updated
                    this.updateValue(query, interpolateParams);
                }
            });
        }

        return this.value;
    }

    private _dispose(): void {
        if (typeof this.onLocalizationChange !== 'undefined') {
            this.onLocalizationChange.unsubscribe();
            this.onLocalizationChange = undefined;
        }
        if (typeof this.onLanguageChange !== 'undefined') {
            this.onLanguageChange.unsubscribe();
            this.onLanguageChange = undefined;
        }
        if (typeof this.onDefaultLanguageChange !== 'undefined') {
            this.onDefaultLanguageChange.unsubscribe();
            this.onDefaultLanguageChange = undefined;
        }
    }

    ngOnDestroy(): void {
        this._dispose();
    }
}
